import apiClient from "./core/ApiClient";
import { AbsenceBalance } from "@/api/AbsenceBalance";
import { AbsencesBalanceStoreRequest } from "@/api/AbsencesBalanceStoreRequest";
import { AbsencesBalanceListResponse } from "@/api/AbsencesBalanceListResponse";
import { AbsencesListAllResponse } from "@/api/AbsencesListAllResponse";
import { User } from "@/api/User";
import { Absence } from "@/api/Absence";
import { AbsencesPreviewRequest } from "@/api/AbsencesPreviewRequest";
import { AbsencesPreviewResponse } from "@/api/AbsencesPreviewResponse";
import { AbsencesStoreRequest } from "@/api/AbsencesStoreRequest";
import { AbsencesListResponse } from "@/api/AbsencesListResponse";
import { formatIso8601, parseIso } from "@/data/DateTimeUtil";
import { addDays } from "date-fns";
import { StoreResponse } from "@/api/StoreResponse";
import { ApprovalDecisionRequest } from "@/api/ApprovalDecisionRequest";
import { PendingInhabitant } from "@/api/Pending";

const findAllAbsences = (
  year: number,
  month: number
): Promise<Map<User, Absence[]>> =>
  apiClient
    .get<AbsencesListAllResponse>(`/api/users/absences/${year}/${month}`)
    .then((r) => {
      // Create the map with initial capacity if we know the size
      const userMap = new Map<User, Absence[]>();

      // Pre-process absences by user ID to avoid repeated filtering
      const absencesByUserId = new Map<number, Absence[]>();
      r.absences.forEach((absence) => {
        if (!absencesByUserId.has(absence.userId)) {
          absencesByUserId.set(absence.userId, []);
        }
        absencesByUserId.get(absence.userId)!.push(absence);
      });

      // Populate the result map with pre-processed data
      r.users.forEach((user) =>
        userMap.set(user, absencesByUserId.get(user.id) || [])
      );

      return userMap;
    });

// from, to in ISO 8601 date format
const findEntries = (
  userId: number,
  from: string,
  to: string
): Promise<Absence[]> =>
  apiClient
    .get<AbsencesListResponse>(
      `/api/users/${userId}/absences?from=${from}&to=${to}`
    )
    .then((r) => r.results.concat([]));

// from, to in ISO 8601 date format
const findAll = (
  userId: number,
  from: string,
  to: string
): Promise<Map<string, Absence[]>> =>
  findEntries(userId, from, to)
    .then((absences) => {
      const map = new Map<string, Absence[]>();
      absences.forEach((a) => {
        const start = parseIso(a.localStartDate);
        const end = parseIso(a.localEndDate);
        let day = start;
        while (day <= end) {
          const key = formatIso8601(day);
          const entries = map.get(key) || [];
          entries.push(a);
          map.set(key, entries);
          day = addDays(day, 1);
        }
      });
      return map;
    });

const previewAbsence = (ps: Absence): Promise<AbsencesPreviewResponse> => {
  const payload: AbsencesPreviewRequest = {
    entry: ps,
  };
  return apiClient.post<AbsencesPreviewRequest, AbsencesPreviewResponse>(
    `/api/users/${ps.userId}/absences/preview`,
    payload
  );
};

const addAbsence = (ps: Absence): Promise<number> => {
  const payload: AbsencesStoreRequest = {
    entry: ps,
  };
  return apiClient
    .post<AbsencesStoreRequest, StoreResponse>(
      `/api/users/${ps.userId}/absences`,
      payload
    )
    .then((r) => r.id);
};

const updateAbsence = (ps: Absence): Promise<number> => {
  const payload: AbsencesStoreRequest = {
    entry: ps,
  };
  return apiClient
    .put<AbsencesStoreRequest, StoreResponse>(
      `/api/users/${ps.userId}/absences`,
      payload
    )
    .then((r) => r.id);
};

const deleteAbsence = (userId: number, id: number): Promise<void> =>
  apiClient.delete<void>(`/api/users/${userId}/absences/${id}`);

const approveAbsence = (userId: number, id: number): Promise<number> =>
  apiClient
    .put<void, StoreResponse>(`/api/users/${userId}/absences/${id}/approve`, undefined)
    .then((r) => r.id);

const rejectAbsence = (userId: number, id: number, reason: string): Promise<number> => {
  const payload: ApprovalDecisionRequest = { reason };
  return apiClient
    .put<ApprovalDecisionRequest, StoreResponse>(`/api/users/${userId}/absences/${id}/reject`, payload)
    .then((r) => r.id);
};

const resubmitAbsence = (ps: Absence): Promise<number> => {
  const payload: AbsencesStoreRequest = {
    entry: {
      ...ps,
      status: PendingInhabitant,
      decidedBy: null,
      decidedAt: null,
      decisionReason: null,
    },
  };
  return apiClient
    .put<AbsencesStoreRequest, StoreResponse>(
      `/api/users/${ps.userId}/absences/${ps.id}/resubmit`,
      payload
    )
    .then((r) => r.id);
};

const addBalance = (ps: AbsenceBalance): Promise<number> => {
  let payload: AbsencesBalanceStoreRequest = {
    entry: ps,
  };
  return apiClient
    .post<AbsencesBalanceStoreRequest, StoreResponse>(
      `/api/users/${ps.userId}/absences/balance`,
      payload
    )
    .then((r) => r.id);
};

const updateBalance = (ps: AbsenceBalance): Promise<number> => {
  let payload: AbsencesBalanceStoreRequest = {
    entry: ps,
  };
  return apiClient
    .put<AbsencesBalanceStoreRequest, StoreResponse>(
      `/api/users/${ps.userId}/absences/balance`,
      payload
    )
    .then((r) => r.id);
};

const removeBalance = (userId: number, id: number): Promise<void> =>
  apiClient.delete<void>(`/api/users/${userId}/absences/balance/${id}`);

const findAllBalance = (userId: number): Promise<AbsenceBalance[]> =>
  apiClient
    .get<AbsencesBalanceListResponse>(`/api/users/${userId}/absences/balance`)
    .then((r) => r.results.concat([]));

export const absenceService = {
  findAllAbsences,
  findEntries,
  findAll,
  previewAbsence,
  addAbsence,
  updateAbsence,
  deleteAbsence,
  approveAbsence,
  rejectAbsence,
  resubmitAbsence,
  addBalance,
  updateBalance,
  removeBalance,
  findAllBalance,
};
